import { isSSR } from '@helpers/ssr';
import React, { useEffect, useState } from 'react';
/**
 * CRO (Convert) test variants - same approach as Milwaukee
 * (Notion: Clients & Projects / TTi / Milwaukee / CRO).
 *
 * Each variant is a component wrapped in withCroVariant, given a croTestId, croVariant and croActive. Convert
 * switches variant with a single line of Variation JS per variant (including the default):
 *
 *   window.croSetVariant('GF-1284', 'v1')
 *
 * which stores the variant in a cookie and notifies mounted variants. The backend (CroService) reads the cookie
 * to server render the active variant on later visits, so content isn't switched out on each load.
 */
/** Mirrors NetC.Groundforce.Core.Cro.CroActive (serialised as a number) - keep the two in sync */
export var CroActive;
(function (CroActive) {
    /** Not the visitor's active variant - hidden */
    CroActive[CroActive["False"] = 0] = "False";
    /** The visitor's active variant - displayed */
    CroActive[CroActive["True"] = 1] = "True";
    /** CMS edit mode - displayed regardless of the active test, so it can be configured */
    CroActive[CroActive["Edit"] = 2] = "Edit";
    /** CMS edit mode for a code-driven test - hidden behind a toggle button */
    CroActive[CroActive["EditHidden"] = 3] = "EditHidden";
})(CroActive || (CroActive = {}));
/** Convert "Code (JavaScript) triggered" goal ids - create the goal in Convert, then add its id here */
export const GOAL_IDS = {
    ContactCta_Click: 1004128283,
    ContactUsFormModal_Submit: 1004128284,
    ContactUsFormModal_Close: 1004128285,
};
/** Must match CroService.CookiePrefix and the inline script in SiteFiles/UserControls/ConvertExperiments.ascx */
export const CRO_COOKIE_PREFIX = 'CroVariant_';
export const CRO_SET_VARIANT_EVENT = 'cro:setvariant';
const CRO_COOKIE_MAX_AGE = 60 * 60 * 24 * 90;
export const getCroCookieName = (croTestId) => `${CRO_COOKIE_PREFIX}${croTestId.replace(/[^A-Za-z0-9_-]/g, '_')}`;
export const getCroVariantFromCookie = (croTestId) => {
    if (isSSR()) {
        return undefined;
    }
    const name = `${getCroCookieName(croTestId)}=`;
    const cookie = document.cookie.split('; ').find((x) => x.startsWith(name));
    return cookie ? decodeURIComponent(cookie.substring(name.length)) : undefined;
};
/**
 * Defines window.croSetVariant if the inline script in ConvertExperiments.ascx hasn't (e.g. in Storybook).
 */
export const installCroSetVariant = () => {
    if (isSSR() || window.croSetVariant) {
        return;
    }
    window.croSetVariant = (croTestId, croVariant) => {
        window.croVariants = { ...window.croVariants, [croTestId]: croVariant };
        document.cookie = `${getCroCookieName(croTestId)}=${encodeURIComponent(croVariant)}; path=/; max-age=${CRO_COOKIE_MAX_AGE}; SameSite=Lax`;
        window.dispatchEvent(new CustomEvent(CRO_SET_VARIANT_EVENT, {
            detail: { croTestId, croVariant },
        }));
    };
};
installCroSetVariant();
/**
 * Records a conversion for a Convert goal. Queues harmlessly when the Convert snippet isn't on the page.
 */
export const triggerCroConversionGoal = (goalId) => {
    if (isSSR() || !goalId) {
        return;
    }
    window._conv_q = window._conv_q || [];
    window._conv_q.push(['triggerConversion', String(goalId)]);
};
const isVisibleByDefault = (croActive) => croActive === CroActive.True || croActive === CroActive.Edit;
/**
 * Only renders the component while its variant is active. Components without a croTestId aren't part of a test
 * and always render.
 */
export function withCroVariant(Component) {
    const CroVariant = (props) => {
        const { croTestId, croVariant, croActive } = props;
        // Starts from the server-rendered value so hydration matches, then follows the cookie / croSetVariant
        const [isActive, setIsActive] = useState(isVisibleByDefault(croActive));
        const [isToggledOn, setIsToggledOn] = useState(false);
        const isEditMode = croActive === CroActive.Edit || croActive === CroActive.EditHidden;
        useEffect(() => {
            if (!croTestId || !croVariant || isEditMode) {
                return;
            }
            // Convert usually calls croSetVariant before this mounts, so its event has already been missed. Prefer the
            // variant it set on this page view - the cookie can be deleted by the consent script while the page loads.
            const currentVariant = window.croVariants?.[croTestId] ?? getCroVariantFromCookie(croTestId);
            if (currentVariant) {
                setIsActive(currentVariant === croVariant);
            }
            const handleSetVariant = (event) => {
                const { detail } = event;
                if (detail?.croTestId === croTestId) {
                    setIsActive(detail.croVariant === croVariant);
                }
            };
            window.addEventListener(CRO_SET_VARIANT_EVENT, handleSetVariant);
            return () => window.removeEventListener(CRO_SET_VARIANT_EVENT, handleSetVariant);
        }, [croTestId, croVariant, isEditMode]);
        if (!croTestId) {
            return React.createElement(Component, { ...props });
        }
        if (croActive === CroActive.EditHidden) {
            return (React.createElement(React.Fragment, null,
                React.createElement("button", { type: "button", onClick: () => setIsToggledOn(!isToggledOn) },
                    isToggledOn ? 'Hide' : 'Show',
                    " ",
                    croVariant,
                    " variant"),
                isToggledOn && React.createElement(Component, { ...props })));
        }
        return isActive ? React.createElement(Component, { ...props }) : null;
    };
    CroVariant.displayName = `withCroVariant(${Component.displayName || Component.name || 'Component'})`;
    return CroVariant;
}
/**
 * Storybook template that mocks the backend and Convert: renders every variant, adds buttons that call
 * window.croSetVariant, and picks the active variant up from the cookie when the story is refreshed.
 */
export function createCroTemplate(Component) {
    const CroTemplate = ({ variants }) => {
        installCroSetVariant();
        return (React.createElement(React.Fragment, null,
            React.createElement("div", { style: { display: 'flex', gap: 8, padding: 8 } }, variants.map((variant) => (React.createElement("button", { key: `${variant.croTestId}-${variant.croVariant}`, type: "button", onClick: () => window.croSetVariant?.(variant.croTestId ?? '', variant.croVariant ?? '') },
                "croSetVariant('",
                variant.croTestId,
                "', '",
                variant.croVariant,
                "')")))),
            variants.map((variant) => (React.createElement(Component, { key: `${variant.croTestId}-${variant.croVariant}`, ...variant })))));
    };
    return CroTemplate;
}
